import { useEffect, useMemo, useState } from 'react'
import api from '../services/api'
import TeamHelmet from '../components/TeamHelmet'
import { getLeagueTeamDisplayName } from '../constants/teamNames'
import { PLAYABLE_TEAM_ABBREVIATIONS } from '../constants/teamAssets'
import './Upload.css'

interface PassingStats {
  attempts: number
  completions: number
  yards: number
  touchdowns: number
  interceptions: number
}

interface RushingStats {
  attempts: number
  yards: number
  touchdowns: number
}

interface ReceivingStats {
  receptions: number
  yards: number
  touchdowns: number
}

interface ReturnStats {
  kickReturns: number
  kickReturnYards: number
  kickReturnTouchdowns: number
  puntReturns: number
  puntReturnYards: number
  puntReturnTouchdowns: number
}

interface DefenseStats {
  tackles: number
  sacks: number
  interceptions: number
  interceptionYards: number
  interceptionTouchdowns: number
}

interface KickingStats {
  extraPointAttempts: number
  extraPointsMade: number
  fieldGoalAttempts: number
  fieldGoalsMade: number
}

interface PuntingStats {
  punts: number
  yards: number
  averageYards: number
}

type PlayerPosition = 'QB' | 'RB' | 'WR' | 'TE' | 'DL' | 'LB' | 'DB' | 'K' | 'P'

interface PlayerStatLine {
  slot: string
  position: PlayerPosition
  name?: string
  passing?: PassingStats
  rushing?: RushingStats
  receiving?: ReceivingStats
  returns?: ReturnStats
  defense?: DefenseStats
  kicking?: KickingStats
  punting?: PuntingStats
}

interface TeamSummary {
  abbreviation: string
  score: number
  quarterScores: number[]
  firstDowns: number
  fumblesTotal: number
  fumblesLost: number
  timeOfPossessionSeconds: number
  fieldGoalBlocks?: number
  players: PlayerStatLine[]
}

interface UploadGameSummary {
  id: number | null
  home: TeamSummary
  away: TeamSummary
}

interface UserOption {
  id: number
  username: string
  discordUsername?: string | null
  displayName: string
  leagueTeamName?: string | null
}

export default function Upload() {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [gameSummary, setGameSummary] = useState<UploadGameSummary | null>(null)
  const [users, setUsers] = useState<UserOption[]>([])
  const [coachOneUserId, setCoachOneUserId] = useState<string>('')
  const [coachTwoUserId, setCoachTwoUserId] = useState<string>('')
  const [coinTossWinnerUserId, setCoinTossWinnerUserId] = useState<string>('')
  const [teamCalled, setTeamCalled] = useState<string>('')
  const [assignmentStatus, setAssignmentStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await api.get('/users')
        const mapped: UserOption[] = (response.data ?? [])
          .map((user: any) => ({
            id: user.id,
            username: user.username,
            discordUsername: user.discordUsername ?? null,
            leagueTeamName: user.leagueTeamName ?? null,
            displayName:
              user.discordGlobalName ??
              user.discordUsername ??
              user.username,
          }))
          .sort((left: UserOption, right: UserOption) => {
            const displayNameComparison = left.displayName.localeCompare(right.displayName, undefined, {
              sensitivity: 'base',
            })

            if (displayNameComparison !== 0) {
              return displayNameComparison
            }

            return left.username.localeCompare(right.username, undefined, {
              sensitivity: 'base',
            })
          })
        setUsers(mapped)
      } catch (error) {
        console.error('Failed to load users:', error)
      }
    }

    loadUsers()
  }, [])

  useEffect(() => {
    setCoachOneUserId('')
    setCoachTwoUserId('')
    setCoinTossWinnerUserId('')
    setTeamCalled('')
    setAssignmentStatus(null)
  }, [gameSummary?.id])

  useEffect(() => {
    if (
      coinTossWinnerUserId &&
      coinTossWinnerUserId !== coachOneUserId &&
      coinTossWinnerUserId !== coachTwoUserId
    ) {
      setCoinTossWinnerUserId('')
    }
  }, [coachOneUserId, coachTwoUserId, coinTossWinnerUserId])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const matchup = useMemo(() => {
    if (!gameSummary) {
      return null
    }

    const home = gameSummary.home
    const away = gameSummary.away

    if (home.score === away.score) {
      return {
        winning: home,
        losing: away,
        isTie: true,
        winningIsHome: true,
      }
    }

    if (home.score > away.score) {
      return {
        winning: home,
        losing: away,
        isTie: false,
        winningIsHome: true,
      }
    }

    return {
      winning: away,
      losing: home,
      isTie: false,
      winningIsHome: false,
    }
  }, [gameSummary])

  const assignmentDisabled =
    assigning ||
    !coachOneUserId ||
    !coachTwoUserId ||
    !coinTossWinnerUserId ||
    !teamCalled ||
    coachOneUserId === coachTwoUserId ||
    !gameSummary?.id ||
    !matchup

  const coinTossOptions = useMemo(() => {
    const options: Array<{ id: string; label: string }> = []

    const coachOne = users.find(user => String(user.id) === coachOneUserId)
    const coachTwo = users.find(user => String(user.id) === coachTwoUserId)

    if (coachOne) {
      options.push({ id: coachOneUserId, label: coachOne.displayName })
    }

    if (coachTwo && coachTwoUserId !== coachOneUserId) {
      options.push({ id: coachTwoUserId, label: coachTwo.displayName })
    }

    return options
  }, [coachOneUserId, coachTwoUserId, users])

  const handleAssignPlayers = async () => {
    if (!gameSummary?.id || !matchup) {
      setAssignmentStatus({
        type: 'error',
        text: 'No game available to assign players.',
      })
      return
    }

    if (!coachOneUserId || !coachTwoUserId || !coinTossWinnerUserId || !teamCalled) {
      setAssignmentStatus({
        type: 'error',
        text: 'Please complete all four dropdowns before saving.',
      })
      return
    }

    if (coachOneUserId === coachTwoUserId) {
      setAssignmentStatus({
        type: 'error',
        text: 'Please choose two different coaches.',
      })
      return
    }

    if (matchup.isTie) {
      setAssignmentStatus({
        type: 'error',
        text: 'Tied games cannot be assigned automatically.',
      })
      return
    }

    try {
      setAssigning(true)
      setAssignmentStatus(null)

      const resolvedWinningUserId = matchup.winningIsHome ? coachOneUserId : coachTwoUserId
      const resolvedLosingUserId = matchup.winningIsHome ? coachTwoUserId : coachOneUserId
      const homeAwaySlot: 'winning' | 'losing' = matchup.winningIsHome ? 'winning' : 'losing'

      await api.post(`/games/${gameSummary.id}/participants`, {
        winningUserId: Number(resolvedWinningUserId),
        losingUserId: Number(resolvedLosingUserId),
        winningTeamAbbreviation: matchup.winning.abbreviation,
        losingTeamAbbreviation: matchup.losing.abbreviation,
        homeAwaySlot,
        coinTossWinnerUserId: Number(coinTossWinnerUserId),
        teamCalled,
      })
      setAssignmentStatus({
        type: 'success',
        text: 'Game saved successfully.',
      })
      setCoachOneUserId('')
      setCoachTwoUserId('')
      setCoinTossWinnerUserId('')
      setTeamCalled('')
    } catch (error: any) {
      setAssignmentStatus({
        type: 'error',
        text:
          error.response?.data?.error ||
          'Failed to save game details. Please try again.',
      })
    } finally {
      setAssigning(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
      setMessage(null)
      setGameSummary(null)
      setCoachOneUserId('')
      setCoachTwoUserId('')
      setCoinTossWinnerUserId('')
      setTeamCalled('')
      setAssignmentStatus(null)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' })
      return
    }

    setUploading(true)
    setMessage(null)
    setGameSummary(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      setMessage({
        type: 'success',
        text: response.data.message || 'File uploaded and processed successfully!',
      })
      setFile(null)
      // Reset file input
      const fileInput = document.getElementById('file-input') as HTMLInputElement
      if (fileInput) fileInput.value = ''

      const returnedGame = response.data?.data?.games?.[0]
      if (returnedGame?.metadata) {
        setGameSummary({
          id: typeof returnedGame.id === 'number' ? returnedGame.id : null,
          home: returnedGame.metadata.home,
          away: returnedGame.metadata.away,
        })
      } else {
        setGameSummary(null)
      }
      setAssignmentStatus(null)
      setCoachOneUserId('')
      setCoachTwoUserId('')
      setCoinTossWinnerUserId('')
      setTeamCalled('')
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload file. Please try again.',
      })
    } finally {
      setUploading(false)
    }
  }

  const renderPassing = (passing?: PassingStats) => {
    if (!passing) return '—'
    const parts = [
      `${passing.completions}/${passing.attempts}`,
      `${passing.yards} yds`,
    ]
    if (passing.touchdowns > 0) parts.push(`${passing.touchdowns} TD`)
    if (passing.interceptions > 0) parts.push(`${passing.interceptions} INT`)
    return parts.join(', ')
  }

  const renderRushing = (rushing?: RushingStats) => {
    if (!rushing) return '—'
    const parts = [`${rushing.attempts} att`, `${rushing.yards} yds`]
    if (rushing.touchdowns > 0) parts.push(`${rushing.touchdowns} TD`)
    return parts.join(', ')
  }

  const renderReceiving = (receiving?: ReceivingStats) => {
    if (!receiving) return '—'
    const parts = [`${receiving.receptions} rec`, `${receiving.yards} yds`]
    if (receiving.touchdowns > 0) parts.push(`${receiving.touchdowns} TD`)
    return parts.join(', ')
  }

  const renderReturns = (returns?: ReturnStats) => {
    if (!returns) return '—'
    const details: string[] = []
    if (returns.kickReturns > 0) {
      const parts = [`KR ${returns.kickReturns}`, `${returns.kickReturnYards} yds`]
      if (returns.kickReturnTouchdowns > 0) parts.push(`${returns.kickReturnTouchdowns} TD`)
      details.push(parts.join(' '))
    }
    if (returns.puntReturns > 0) {
      const parts = [`PR ${returns.puntReturns}`, `${returns.puntReturnYards} yds`]
      if (returns.puntReturnTouchdowns > 0) parts.push(`${returns.puntReturnTouchdowns} TD`)
      details.push(parts.join(' '))
    }
    return details.length > 0 ? details.join(' | ') : '—'
  }

  const renderDefense = (defense?: DefenseStats) => {
    if (!defense) return '—'
    const parts = [`${defense.tackles} Tk`]
    if (defense.sacks > 0) parts.push(`${defense.sacks} Sk`)
    if (defense.interceptions > 0) {
      parts.push(`${defense.interceptions} INT`)
      if (defense.interceptionYards !== 0) {
        parts.push(`${defense.interceptionYards} yds`)
      }
      if (defense.interceptionTouchdowns > 0) {
        parts.push(`${defense.interceptionTouchdowns} TD`)
      }
    }
    return parts.join(', ')
  }

  const renderKicking = (kicking?: KickingStats) => {
    if (!kicking) return '—'
    return `XP ${kicking.extraPointsMade}/${kicking.extraPointAttempts}, FG ${kicking.fieldGoalsMade}/${kicking.fieldGoalAttempts}`
  }

  const renderPunting = (punting?: PuntingStats) => {
    if (!punting) return '—'
    const parts = [`${punting.punts} punts`, `${punting.yards} yds`]
    if (punting.punts > 0) {
      parts.push(`${punting.averageYards.toFixed(1)} avg`)
    }
    return parts.join(', ')
  }

  const renderTeamPlayers = (team: TeamSummary) => {
    if (!team.players || team.players.length === 0) {
      return null
    }

    const playersWithStats = team.players.filter(player => {
      const hasOffense =
        (player.passing && player.passing.attempts > 0) ||
        (player.rushing && player.rushing.attempts > 0) ||
        (player.receiving && player.receiving.receptions > 0) ||
        (player.returns &&
          (player.returns.kickReturns > 0 || player.returns.puntReturns > 0))
      const hasDefense =
        player.defense &&
        (player.defense.tackles > 0 ||
          player.defense.sacks > 0 ||
          player.defense.interceptions > 0)
      const hasSpecial =
        (player.kicking &&
          (player.kicking.extraPointAttempts > 0 || player.kicking.fieldGoalAttempts > 0)) ||
        (player.punting && player.punting.punts > 0)

      return hasOffense || hasDefense || hasSpecial
    })

    if (playersWithStats.length === 0) {
      return null
    }

    return (
      <div className="player-stats">
        <h4>{team.abbreviation} Player Stats</h4>
        <div className="player-table-wrapper">
          <table className="player-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Slot</th>
                <th>Pos</th>
                <th>Passing</th>
                <th>Rushing</th>
                <th>Receiving</th>
                <th>Returns</th>
                <th>Defense</th>
                <th>Kicking</th>
                <th>Punting</th>
              </tr>
            </thead>
            <tbody>
              {playersWithStats.map(player => (
                <tr key={`${team.abbreviation}-${player.slot}`}>
                  <td>{player.name ?? player.slot}</td>
                  <td>{player.slot}</td>
                  <td>{player.position}</td>
                  <td>{renderPassing(player.passing)}</td>
                  <td>{renderRushing(player.rushing)}</td>
                  <td>{renderReceiving(player.receiving)}</td>
                  <td>{renderReturns(player.returns)}</td>
                  <td>{renderDefense(player.defense)}</td>
                  <td>{renderKicking(player.kicking)}</td>
                  <td>{renderPunting(player.punting)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="upload">
      <h1>Upload Results</h1>
      <div className="upload-container">
        <div className="game-upload-section">
          <h2>Game Results</h2>
          <form onSubmit={handleUpload} className="upload-form">
            <div className="file-input-wrapper">
            <label htmlFor="file-input" className="file-label">
              {file ? file.name : 'Choose a file'}
            </label>
            <input
              id="file-input"
              type="file"
              onChange={handleFileChange}
              className="file-input"
              accept=".ns0,.ns1,.ns2,.ns3,.ns4,.ns5,.ns6,.ns7,.ns8,.ns9,.nst"
            />
          </div>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload & Process'}
          </button>
          </form>
          
          {message && (
            <div className={`message message-${message.type}`}>
              {message.text}
            </div>
          )}

          {gameSummary && (
          <div className="game-summary">
            <h3>Latest Upload Summary</h3>
            <div className="scoreboard">
              <div className="team-card">
                <div className="team-header">
                  <span className="team-name">
                    <TeamHelmet
                      abbreviation={gameSummary.home.abbreviation}
                      size="sm"
                      className="team-helmet"
                    />
                    <span>{gameSummary.home.abbreviation}</span>
                  </span>
                  <span className="team-score">{gameSummary.home.score}</span>
                </div>
                <div className="team-stats-grid">
                  <div>
                    <span className="label">First Downs</span>
                    <span className="value">{gameSummary.home.firstDowns}</span>
                  </div>
                  <div>
                    <span className="label">Fumbles</span>
                    <span className="value">
                      {gameSummary.home.fumblesLost}/{gameSummary.home.fumblesTotal}
                    </span>
                  </div>
                  <div>
                    <span className="label">Time of Possession</span>
                    <span className="value">{formatTime(gameSummary.home.timeOfPossessionSeconds)}</span>
                  </div>
                </div>
              </div>
              <div className="team-card">
                <div className="team-header">
                  <span className="team-name">
                    <TeamHelmet
                      abbreviation={gameSummary.away.abbreviation}
                      size="sm"
                      className="team-helmet"
                    />
                    <span>{gameSummary.away.abbreviation}</span>
                  </span>
                  <span className="team-score">{gameSummary.away.score}</span>
                </div>
                <div className="team-stats-grid">
                  <div>
                    <span className="label">First Downs</span>
                    <span className="value">{gameSummary.away.firstDowns}</span>
                  </div>
                  <div>
                    <span className="label">Fumbles</span>
                    <span className="value">
                      {gameSummary.away.fumblesLost}/{gameSummary.away.fumblesTotal}
                    </span>
                  </div>
                  <div>
                    <span className="label">Time of Possession</span>
                    <span className="value">{formatTime(gameSummary.away.timeOfPossessionSeconds)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="quarter-summary">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                    <th>Q4</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{gameSummary.home.abbreviation}</td>
                    {gameSummary.home.quarterScores.map((score, index) => (
                      <td key={`home-q${index}`}>{score}</td>
                    ))}
                    <td className="total">{gameSummary.home.score}</td>
                  </tr>
                  <tr>
                    <td>{gameSummary.away.abbreviation}</td>
                    {gameSummary.away.quarterScores.map((score, index) => (
                      <td key={`away-q${index}`}>{score}</td>
                    ))}
                    <td className="total">{gameSummary.away.score}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {users.length > 0 && matchup && (
              <div className="player-assignment">
                <h4>Coach Assignment</h4>
                <p className="assignment-hint">
                  {matchup.isTie
                    ? 'Select the coach for each team in the uploaded order.'
                    : `Select the coach for each team in uploaded order. The winner will be inferred from the score automatically.`}
                </p>
                <div className="assignment-controls">
                  <div className="assignment-field">
                    <label htmlFor="coach-one-select">
                      P1 Coach - {gameSummary.home.abbreviation}
                    </label>
                    <select
                      id="coach-one-select"
                      value={coachOneUserId}
                      onChange={(event) => {
                        setCoachOneUserId(event.target.value)
                        setAssignmentStatus(null)
                      }}
                    >
                      <option value="">Select a user</option>
                      {users.map(user => (
                        <option key={`coach-one-${user.id}`} value={user.id}>
                          {user.displayName}{user.leagueTeamName ? ` - ${getLeagueTeamDisplayName(user.leagueTeamName)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="assignment-field">
                    <label htmlFor="coach-two-select">
                      P2 Coach - {gameSummary.away.abbreviation}
                    </label>
                    <select
                      id="coach-two-select"
                      value={coachTwoUserId}
                      onChange={(event) => {
                        setCoachTwoUserId(event.target.value)
                        setAssignmentStatus(null)
                      }}
                    >
                      <option value="">Select a user</option>
                      {users.map(user => (
                        <option key={`coach-two-${user.id}`} value={user.id}>
                          {user.displayName}{user.leagueTeamName ? ` - ${getLeagueTeamDisplayName(user.leagueTeamName)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="assignment-field">
                    <label htmlFor="coin-toss-winner-select">Coin Toss Winner</label>
                    <select
                      id="coin-toss-winner-select"
                      value={coinTossWinnerUserId}
                      onChange={(event) => {
                        setCoinTossWinnerUserId(event.target.value)
                        setAssignmentStatus(null)
                      }}
                    >
                      <option value="">Select a coach</option>
                      {coinTossOptions.map((option) => (
                        <option key={`coin-toss-${option.id}`} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="assignment-field">
                    <label htmlFor="team-called-select">Team Called</label>
                    <select
                      id="team-called-select"
                      value={teamCalled}
                      onChange={(event) => {
                        setTeamCalled(event.target.value)
                        setAssignmentStatus(null)
                      }}
                    >
                      <option value="">Select a team</option>
                      {PLAYABLE_TEAM_ABBREVIATIONS.map((abbreviation) => (
                        <option key={`team-called-${abbreviation}`} value={abbreviation}>
                          {abbreviation}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {assignmentStatus && (
                  <div className={`message message-${assignmentStatus.type}`}>
                    {assignmentStatus.text}
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary assign-btn"
                  onClick={handleAssignPlayers}
                  disabled={assignmentDisabled}
                >
                  {assigning ? 'Saving...' : 'Save Game'}
                </button>
              </div>
            )}

            {users.length === 0 && (
              <div className="player-assignment">
                <h4>Coach Assignment</h4>
                <div className="message message-error">
                  No Discord users found. Once players sign in with Discord, you can assign them to uploaded games.
                </div>
              </div>
            )}

            {renderTeamPlayers(gameSummary.home)}
            {renderTeamPlayers(gameSummary.away)}
          </div>
          )}
        </div>

        <div className="upload-info">
          <h3>Upload Instructions</h3>
          <ul>
            <li><strong>Game Results:</strong> Upload Nestopia save states (.ns0 – .ns9 / .nst) directly from your game</li>
            <li>Only one completed game per save state upload</li>
            <li>After upload, assign the coach for team 1 and team 2 in the uploaded order</li>
            <li>Duplicate files will be automatically detected</li>
            <li>Results will be processed and standings updated automatically</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

